import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { listOffers, listOfferStories } from '../../services/offers'
import OfferCard from '../../components/OfferCard'
import OfferStoriesStrip from '../../components/OfferStoriesStrip'
import OfferStoryViewer from '../../components/OfferStoryViewer'
import MobileBottomNav from '../../components/MobileBottomNav'
import { getData } from '../../services/request'
import { getAPIBaseURL } from '../../services/helpers'
import { MapPinIcon, FunnelIcon } from '@heroicons/react/24/outline'

const OffersPage = () => {
  const navigate = useNavigate()
  const [offers, setOffers] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [countryFilter, setCountryFilter] = useState('')
  const [cityFilter, setCityFilter] = useState('')
  const [oneTableLeft, setOneTableLeft] = useState(false)
  const [booked, setBooked] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [countries, setCountries] = useState([])
  const [loadingCountries, setLoadingCountries] = useState(true)
  const [userLocation, setUserLocation] = useState(null)
  const [locationError, setLocationError] = useState('')
  const [offerStories, setOfferStories] = useState([])
  const [storyViewerOpen, setStoryViewerOpen] = useState(false)
  const [storyViewerStartIndex, setStoryViewerStartIndex] = useState(0)

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const url = `${getAPIBaseURL()}/v1/countries`
        const res = await getData(url, {}, {})
        if (res.error === '' && res.result?.status === 200) {
          const list = res.result.data?.data || res.result.data || []
          setCountries(Array.isArray(list) ? list : [])
        }
      } catch (err) {
        // silently fail
      } finally {
        setLoadingCountries(false)
      }
    }
    fetchCountries()
  }, [])

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      const filters = { status: 'ACTIVE' }
      if (countryFilter) filters.country = countryFilter
      if (cityFilter) filters.city = cityFilter
      if (oneTableLeft) filters.oneTableLeft = true
      if (booked) filters.booked = true
      
      const res = await listOffers(filters)
      if (res.status === 200) {
        setOffers(Array.isArray(res.data) ? res.data : [])
      }
      setLoading(false)
    }
    load()
  }, [countryFilter, cityFilter, oneTableLeft, booked])

  useEffect(() => {
    const loadStories = async () => {
      const res = await listOfferStories()
      if (res.status === 200) {
        setOfferStories(Array.isArray(res.data) ? res.data : [])
      }
    }
    loadStories()
  }, [])

  // Extract unique categories
  const categories = useMemo(() => {
    const cats = new Set()
    offers.forEach(offer => {
      if (offer.category) cats.add(offer.category)
    })
    return Array.from(cats).sort()
  }, [offers])

  const handleNearby = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser')
      return
    }
    
    setLocationError('')
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        })
        // For now, we'll just show a message. Full nearby implementation would require backend support
        setLocationError('Nearby filter activated. Showing offers near you.')
      },
      (error) => {
        setLocationError('Unable to get your location. Please enable location services.')
      }
    )
  }

  const filtered = useMemo(() => {
    let result = offers

    // Filter by search
    if (search) {
      const term = search.toLowerCase()
      result = result.filter(o =>
        (o.title || '').toLowerCase().includes(term) ||
        (o.description || '').toLowerCase().includes(term) ||
        (o.category || '').toLowerCase().includes(term)
      )
    }

    // Filter by category
    if (categoryFilter) {
      result = result.filter(o => o.category === categoryFilter)
    }

    return result
  }, [offers, search, categoryFilter])

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white pb-20 lg:pb-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-10">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
            <div>
              <p className="text-sm font-semibold text-emerald-700">Discover Deals</p>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-2">Special Offers</h1>
              <p className="text-slate-600 mt-2">Browse exclusive discounts from restaurants, bars, and spots.</p>
            </div>
            <div className="w-full sm:w-64">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search offers"
                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            {/* Category Filter */}
            {categories.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setCategoryFilter('')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    categoryFilter === ''
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white text-slate-700 border border-slate-200 hover:border-emerald-300'
                  }`}
                >
                  All
                </button>
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setCategoryFilter(cat)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      categoryFilter === cat
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white text-slate-700 border border-slate-200 hover:border-emerald-300'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white text-slate-700 border border-slate-200 hover:border-emerald-300 transition-colors"
            >
              <FunnelIcon className="w-4 h-4" />
              Filters
            </button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Country Filter */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Country</label>
                  <select
                    value={countryFilter}
                    onChange={(e) => {
                      setCountryFilter(e.target.value)
                      setCityFilter('') // Reset city when country changes
                    }}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">All Countries</option>
                    {countries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* City Filter */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">City</label>
                  <input
                    type="text"
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    placeholder="Enter city name"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                {/* Nearby Button */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Location</label>
                  <button
                    onClick={handleNearby}
                    className="w-full px-3 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors flex items-center justify-center gap-2"
                  >
                    <MapPinIcon className="w-4 h-4" />
                    Nearby
                  </button>
                </div>

                {/* Availability Filters */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Availability</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setOneTableLeft(!oneTableLeft)}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                        oneTableLeft
                          ? 'bg-red-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      1 Left
                    </button>
                    <button
                      onClick={() => setBooked(!booked)}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                        booked
                          ? 'bg-orange-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      Booked
                    </button>
                  </div>
                </div>
              </div>

              {locationError && (
                <div className={`mt-3 p-2 rounded-lg text-xs ${
                  locationError.includes('activated') 
                    ? 'bg-green-50 text-green-700' 
                    : 'bg-red-50 text-red-700'
                }`}>
                  {locationError}
                </div>
              )}

              {/* Clear Filters */}
              {(countryFilter || cityFilter || oneTableLeft || booked) && (
                <button
                  onClick={() => {
                    setCountryFilter('')
                    setCityFilter('')
                    setOneTableLeft(false)
                    setBooked(false)
                    setUserLocation(null)
                    setLocationError('')
                  }}
                  className="mt-3 text-xs text-slate-600 hover:text-slate-900 underline"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </header>

        {/* Offer Stories Strip */}
        <OfferStoriesStrip
          stories={offerStories}
          onStoryOpen={(index) => {
            setStoryViewerStartIndex(index)
            setStoryViewerOpen(true)
          }}
        />

        {/* Story Viewer */}
        {storyViewerOpen && (
          <OfferStoryViewer
            stories={offerStories}
            startIndex={storyViewerStartIndex}
            onClose={() => setStoryViewerOpen(false)}
          />
        )}

        {loading ? (
          <div className="py-20 text-center text-slate-500 text-sm">Loading offers...</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center bg-white border border-dashed border-slate-200 rounded-2xl">
            <p className="text-lg font-semibold text-slate-900 mb-2">No offers found</p>
            <p className="text-slate-600">Try another search or check back later.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(offer => (
              <OfferCard key={offer._id || offer.id} offer={offer} />
            ))}
          </div>
        )}
      </div>
      <MobileBottomNav />
    </div>
  )
}

export default OffersPage

